import { supabase } from "@/integrations/supabase/client";

export interface CreateSellerBuyerData {
  sellerId: string;
  fullName: string;
  companyName?: string;
  email?: string;
  phoneNumber: string;
  dealsAnnually: number;
  preferredMarkets: string[];
  purchasingType: string[];
  averageCloseTime?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  isPrivate: boolean;
  notes?: string;
}

export const sellerCreatedBuyerService = {
  /**
   * Create a new manually-entered buyer
   */
  async createBuyer(data: CreateSellerBuyerData) {
    const { data: buyer, error } = await (supabase as any)
      .from('seller_created_buyers')
      .insert({
        seller_id: data.sellerId,
        full_name: data.fullName,
        company_name: data.companyName || null,
        email: data.email || null,
        phone_number: data.phoneNumber,
        deals_annually: data.dealsAnnually,
        preferred_markets: data.preferredMarkets,
        purchasing_type: data.purchasingType,
        average_close_time: data.averageCloseTime || null,
        address: data.address || null,
        city: data.city || null,
        state: data.state || null,
        zip_code: data.zipCode || null,
        is_private: data.isPrivate,
        notes: data.notes || null,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating seller buyer:', error);
      return { data: null, error: error.message };
    }

    return { data: buyer, error: null };
  },

  /**
   * Add seller-created buyer to a list
   */
  async addBuyerToList(listId: string, sellerCreatedBuyerId: string, notes?: string) {
    // Check if already in list
    const { data: existing } = await (supabase as any)
      .from('seller_buyer_list_items')
      .select('id')
      .eq('list_id', listId)
      .eq('seller_created_buyer_id', sellerCreatedBuyerId)
      .maybeSingle();

    if (existing) {
      return { success: false, error: 'This buyer is already in the list' };
    }

    const { error } = await (supabase as any)
      .from('seller_buyer_list_items')
      .insert({
        list_id: listId,
        seller_created_buyer_id: sellerCreatedBuyerId,
        buyer_id: null,
        notes: notes || null,
      });

    if (error) {
      console.error('Error adding buyer to list:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  },

  /**
   * Get seller's created buyers
   */
  async getSellerBuyers(sellerId: string) {
    const { data, error } = await (supabase as any)
      .from('seller_created_buyers')
      .select('*')
      .eq('seller_id', sellerId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching seller buyers:', error);
      return { data: [], error: error.message };
    }

    return { data, error: null };
  },

  /**
   * Update seller-created buyer
   */
  async updateBuyer(buyerId: string, updates: Partial<CreateSellerBuyerData>) {
    const { error } = await (supabase as any)
      .from('seller_created_buyers')
      .update({
        full_name: updates.fullName,
        company_name: updates.companyName || null,
        email: updates.email || null,
        phone_number: updates.phoneNumber,
        deals_annually: updates.dealsAnnually,
        preferred_markets: updates.preferredMarkets,
        purchasing_type: updates.purchasingType,
        average_close_time: updates.averageCloseTime || null,
        address: updates.address || null,
        city: updates.city || null,
        state: updates.state || null,
        zip_code: updates.zipCode || null,
        is_private: updates.isPrivate,
        notes: updates.notes || null,
      })
      .eq('id', buyerId);

    if (error) {
      console.error('Error updating seller buyer:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  },

  /**
   * Delete seller-created buyer
   */
  async deleteBuyer(buyerId: string) {
    const { error } = await (supabase as any)
      .from('seller_created_buyers')
      .delete()
      .eq('id', buyerId);

    if (error) {
      console.error('Error deleting seller buyer:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  }
};
